import React, { useRef, useState } from 'react';
import { Camera, Upload, CheckCircle2, Loader2, RefreshCw, XCircle } from 'lucide-react';
import { useInspectionStore } from '@/context/inspectionStore';
import { VEHICLE_PHOTO_PROMPTS, getPromptsForType } from '@/utils/vehiclePhotoPrompts';
import { applyPhotoToChecks } from '@/services/photoCheckMapper';
import { validateVehicleConsistency } from '@/services/aiAnalysis';
import { generateImageHash } from '@/services/imageHash';
import { PhotoReuseWarning } from '@/components/common/PhotoReuseWarning';
import { authFetch } from '@/utils/authFetch';
import type { InspectionType, VehiclePhotoPrompt, InspectionPhoto, VehicleValidationResult } from '@/types';
import toast from 'react-hot-toast';
import { compareWithPreviousPhoto } from '@/services/aiAnalysis';

interface VehiclePhotoSectionProps {
  onComplete: () => void;
}

export function VehiclePhotoSection({ onComplete }: VehiclePhotoSectionProps) {
  const inspection = useInspectionStore(s => s.activeInspection);
  const vehiclePhotos = useInspectionStore(s => s.vehiclePhotos);
  const setVehiclePhoto = useInspectionStore(s => s.setVehiclePhoto);
  const setCheckStatus = useInspectionStore(s => s.setCheckStatus);

  const [analyzing, setAnalyzing] = useState<VehiclePhotoPrompt | null>(null);
  const [duplicateWarning, setDuplicateWarning] = useState<{
    promptId: VehiclePhotoPrompt;
    file: File;
    matchedAt: string;
    matchedPromptId: string;
  } | null>(null);

  const allPrompts = inspection ? getPromptsForType(inspection.type as InspectionType) : [];
  const requiredPrompts = allPrompts.filter(p => p.required);
  const optionalPrompts = allPrompts.filter(p => !p.required);

  const completedRequired = requiredPrompts.filter(p =>
    vehiclePhotos.some(vp => vp.promptId === p.id && vp.photo)
  ).length;

  const allRequiredDone = completedRequired === requiredPrompts.length;

  const [wrongVehicleWarning, setWrongVehicleWarning] = React.useState<{
    promptId: VehiclePhotoPrompt;
    file: File;
    hash: string;
    reasoning: string;
  } | null>(null);

  async function handlePhoto(promptId: VehiclePhotoPrompt, file: File) {
    if (!inspection) return;
    setAnalyzing(promptId);

    try {
      const hash = await generateImageHash(file);

      // If offline, skip all API calls and save locally immediately
      if (!navigator.onLine) {
        await processPhoto(promptId, file, hash);
        return;
      }

      // Online — check for duplicate hash before proceeding
      try {
        const hashCheckRes = await authFetch('/api/photos/check-hash', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ hash, vehicleId: inspection.vehicleId, promptId }),
        });
        const hashCheck = await hashCheckRes.json();

        if (hashCheck.isDuplicate) {
          setAnalyzing(null);
          setDuplicateWarning({
            promptId, file,
            matchedAt: hashCheck.matchedAt,
            matchedPromptId: hashCheck.matchedPromptId,
          });
          return;
        }
      } catch (hashErr) {
        // Hash check failed (e.g. network dropped between online check and request)
        // Continue without duplicate detection rather than blocking the user
        console.warn('[Photo] Hash check failed, continuing without duplicate detection:', hashErr);
      }

      await processPhoto(promptId, file, hash);
    } catch (err: any) {
      console.error('[Photo] handlePhoto failed for promptId:', promptId);
      console.error('[Photo] Error message:', err?.message ?? err);
      toast.error(`Failed to process photo: ${err?.message ?? 'Unknown error'}. Check browser console for details.`);
      setAnalyzing(null);
    }
  }

  async function processPhoto(promptId: VehiclePhotoPrompt, file: File, hash: string) {
    if (!inspection) return;
    setAnalyzing(promptId);

    try {
      const isOnline = navigator.onLine;

      if (!isOnline) {
        const blobUri = URL.createObjectURL(file);
        const photo: InspectionPhoto = {
          id: `photo-${Date.now()}`,
          uri: blobUri,
          timestamp: new Date().toISOString(),
          pendingUpload: true,
          pendingFile: file,
          imageHash: hash,
        };
        setVehiclePhoto(promptId, photo);
        toast('Photo saved locally — will be analyzed when reconnected', { icon: '📷' });
        return;
      }

      // Only validate vehicle consistency for exterior photos that would
      // show identifying characteristics of the vehicle (body type, shape, colour).
      // Interior, undercarriage, fluid, and equipment photos cannot reasonably
      // be used to verify which vehicle is being inspected.
      const SKIP_VEHICLE_VALIDATION = new Set<VehiclePhotoPrompt>([
        'interior-cab-driver',
        'interior-cab-passenger',
        'interior-cargo',
        'dashboard-gauges',
        'engine-oil',
        'coolant-reservoir',
        'brake-fluid',
        'transmission-fluid',
        'washer-fluid',
        'underside',
        'belts-hoses',
        'registration-cards',
        'fire-extinguisher',
        'warning-triangles',
        'hi-vis-vest',
        'traffic-cones',
        'first-aid-kit',
        'ladder-rack',
        'close-up-damage-1',
        'close-up-damage-2',
        'other-feature',
      ]);

      const referenceDescription = useInspectionStore.getState().vehicleReferenceDescription;
      const expectedVehicle = useInspectionStore.getState().expectedVehicleDescription ?? undefined;

      let validation: VehicleValidationResult | null = null;

      // Prompts that show only a partial view of the vehicle —
      // require higher confidence before blocking as wrong vehicle.
      const HIGH_CONFIDENCE_PROMPTS = new Set<VehiclePhotoPrompt>([
        'left-mirror',
        'right-mirror',
        'windshield',
      ]);

      const wrongVehicleThreshold = HIGH_CONFIDENCE_PROMPTS.has(promptId) ? 0.95 : 0.85;

      if (!SKIP_VEHICLE_VALIDATION.has(promptId)) {
        console.log('[Validation] Running vehicle check for:', promptId);
        validation = await validateVehicleConsistency(
          file,
          referenceDescription ?? undefined,
          expectedVehicle
        );

        // Block if wrong vehicle detected — force retake
        if (!validation.isSameVehicle && validation.confidence > wrongVehicleThreshold) {
          setAnalyzing(null);
          setWrongVehicleWarning({
            promptId,
            file,
            hash,
            reasoning: validation.reasoning,
          });
          return;
        }

        // Save reference description from first valid exterior photo
        if (!referenceDescription && validation.vehicleDescription) {
          useInspectionStore.getState().setVehicleReferenceDescription(validation.vehicleDescription);
        }
      } else {
        console.log('[Validation] Skipping vehicle check for interior/detail prompt:', promptId);
      }

      const { photo, updatedChecks, pendingUpload } = await applyPhotoToChecks(file, promptId, inspection.categories, inspection.type);

      // If upload failed due to network (weak signal) — saved locally for later sync
      if (pendingUpload) {
        setVehiclePhoto(promptId, photo);
        toast('Weak signal detected — photo saved locally and will be analyzed when reconnected', { icon: '📶' });
        return;
      }
      photo.vehicleValidation = validation ?? undefined;
      photo.isInvalid = false;
      photo.imageHash = hash;

      try {
        const prevRes = await authFetch(
          `/api/inspections/previous-photo/${inspection.vehicleId}/${inspection.type}/${promptId}`
        );
        const prevData = await prevRes.json();

        if (prevData.photo?.uri) {
          const promptLabel = getPromptsForType(inspection.type as any)
            .find(p => p.id === promptId)?.label ?? promptId;

          const comparison = await compareWithPreviousPhoto(
            file,
            prevData.photo.uri,
            promptLabel,
            inspection.type,
          );

          photo.comparisonNotes = comparison;

          if (comparison.hasChanges && comparison.severity !== 'none') {
            // Only show toast for actual damage changes — not duplicate detection
            // (duplicate reuse is already caught by the hash check before upload)
            const isDuplicateFlag = comparison.changes.some(c =>
              c.toUpperCase().includes('DUPLICATE PHOTO DETECTED')
            );

            if (!isDuplicateFlag) {
              const severityLabel = comparison.severity === 'significant' ? '⚠️' :
                comparison.severity === 'moderate' ? '🔶' : 'ℹ️';
              toast(`${severityLabel} ${promptLabel}: ${comparison.summary}`, {
                duration: 5000,
              });
            }
          }
        }
      } catch (err) {
        console.error('Comparison fetch error:', err);
        // Non-fatal — continue without comparison
      }

      setVehiclePhoto(promptId, photo);

      // Save hash — non-fatal if it fails
      try {
        await authFetch('/api/photos/save-hash', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            hash,
            vehicleId: inspection.vehicleId,
            promptId,
            inspectionId: inspection.id,
          }),
        });
      } catch (hashSaveErr) {
        console.warn('[Photo] Failed to save hash, continuing:', hashSaveErr);
      }

      for (const { categoryId, checkId, status, aiReasoning } of updatedChecks) {
        setCheckStatus(categoryId, checkId, status, aiReasoning);
      }

      if (updatedChecks.length > 0) {
        const failCount = updatedChecks.filter(c => c.status === 'fail').length;
        const passCount = updatedChecks.filter(c => c.status === 'pass').length;
        if (failCount > 0) toast.error(`${failCount} check${failCount > 1 ? 's' : ''} failed`);
        else toast.success(`${passCount} check${passCount > 1 ? 's' : ''} passed`);
      }
    } catch (err: any) {
      console.error('[Photo] processPhoto failed for promptId:', promptId);
      console.error('[Photo] Error message:', err?.message ?? err);
      console.error('[Photo] Error stack:', err?.stack ?? 'no stack');
      toast.error(`Failed to process photo: ${err?.message ?? 'Unknown error'}. Check browser console for details.`);
    } finally {
      setAnalyzing(null);
    }
  }

  return (
    <div className="mx-auto max-w-lg px-4 py-6 pb-32 dark:bg-gray-800 min-h-full">

      {/* ── Duplicate photo warning modal ── */}
      {duplicateWarning && (
        <PhotoReuseWarning
          promptLabel={
            VEHICLE_PHOTO_PROMPTS.find(p => p.id === duplicateWarning.promptId)?.label
            ?? duplicateWarning.promptId
          }
          matchedAt={duplicateWarning.matchedAt}
          onRetake={() => setDuplicateWarning(null)}
        />
      )}

      {/* Wrong vehicle warning — force retake */}
      {wrongVehicleWarning && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4">
          <div className="w-full max-w-sm rounded-2xl bg-white dark:bg-gray-800 shadow-2xl p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-status-fail/10">
                <XCircle className="h-5 w-5 text-status-fail" />
              </div>
              <div>
                <h3 className="font-bold text-gray-900 dark:text-gray-100">Wrong Vehicle Detected</h3>
                <p className="text-xs text-gray-500 dark:text-gray-400">A new photo is required</p>
              </div>
            </div>

            <p className="text-sm text-gray-700 dark:text-gray-300 mb-2">
              The photo you submitted does not appear to show{' '}
              <span className="font-semibold">
                {useInspectionStore.getState().expectedVehicleDescription ?? 'the correct vehicle'}
              </span>.
            </p>

            <div className="rounded-lg bg-gray-50 dark:bg-gray-700 px-3 py-2 mb-4">
              <p className="text-xs text-gray-500 dark:text-gray-400 italic">
                "{wrongVehicleWarning.reasoning}"
              </p>
            </div>

            <p className="text-xs text-gray-500 dark:text-gray-400 mb-5">
              Please take a new photo of the correct vehicle to continue.
            </p>

            <button
              onClick={() => setWrongVehicleWarning(null)}
              className="btn-primary w-full"
            >
              <RefreshCw className="h-4 w-4" />
              Retake Photo
            </button>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="mb-2">
        <h2 className="text-xl font-bold text-wire3-navy dark:text-wire3-green">Vehicle Photos</h2>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
          Take photos of each area. AI will automatically assess related inspection checks.
        </p>
      </div>

      {/* Progress */}
      <div className="mb-5 rounded-xl bg-wire3-navy/5 dark:bg-gray-700 px-4 py-3">
        <div className="flex items-center justify-between">
          <p className="text-sm font-semibold text-wire3-navy dark:text-wire3-green">
            {completedRequired} / {requiredPrompts.length} required photos
          </p>
          {allRequiredDone && (
            <span className="flex items-center gap-1 text-xs font-semibold text-status-pass">
              <CheckCircle2 className="h-3.5 w-3.5" />
              All required done
            </span>
          )}
        </div>
        <div className="mt-2 h-2 w-full overflow-hidden rounded-full bg-gray-200 dark:bg-gray-600">
          <div
            className="h-full rounded-full bg-wire3-green transition-all"
            style={{ width: `${(completedRequired / requiredPrompts.length) * 100}%` }}
          />
        </div>
      </div>

      {/* Required photos */}
      <h3 className="mb-3 text-xs font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-500">
        Required Photos
      </h3>
      <div className="grid grid-cols-2 gap-3 mb-6">
        {requiredPrompts.map(prompt => (
          <PhotoPromptCard
            key={prompt.id}
            prompt={prompt}
            photo={vehiclePhotos.find(p => p.promptId === prompt.id)?.photo ?? null}
            analyzing={analyzing === prompt.id}
            onPhoto={(file) => handlePhoto(prompt.id, file)}
          />
        ))}
      </div>

      {/* Optional photos */}
      <h3 className="mb-3 text-xs font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-500">
        Optional Photos
      </h3>
      <div className="grid grid-cols-2 gap-3 mb-6">
        {optionalPrompts.map(prompt => (
          <PhotoPromptCard
            key={prompt.id}
            prompt={prompt}
            photo={vehiclePhotos.find(p => p.promptId === prompt.id)?.photo ?? null}
            analyzing={analyzing === prompt.id}
            onPhoto={(file) => handlePhoto(prompt.id, file)}
          />
        ))}
      </div>

      {/* Continue button */}
      <div className="fixed bottom-16 left-0 right-0 z-20 border-t border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 px-4 py-3 sm:bottom-0">
        <div className="mx-auto max-w-lg">
          {!allRequiredDone && (
            <p className="mb-2 text-center text-xs text-status-warning">
              {requiredPrompts.length - completedRequired} required photo{requiredPrompts.length - completedRequired > 1 ? 's' : ''} remaining
            </p>
          )}
          <button
            onClick={onComplete}
            disabled={!allRequiredDone}
            className="btn-primary w-full disabled:opacity-50"
          >
            Continue to Manual Checks
          </button>

          {import.meta.env.DEV && (
            <button
              onClick={onComplete}
              className="mt-2 w-full rounded-lg border border-dashed border-gray-300 dark:border-gray-600 py-2 text-xs text-gray-400 hover:border-gray-400 hover:text-gray-500"
            >
              [DEV] Skip photos → manual checks
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Photo Prompt Card ─────────────────────────────────────

interface PhotoPromptCardProps {
  prompt: { id: VehiclePhotoPrompt; label: string; description: string; required: boolean; icon: string };
  photo: any | null;
  analyzing: boolean;
  onPhoto: (file: File) => void;
}

function PhotoPromptCard({ prompt, photo, analyzing, onPhoto }: PhotoPromptCardProps) {
  const cameraRef = useRef<HTMLInputElement>(null);
  const uploadRef = useRef<HTMLInputElement>(null);

  const hasDamage = photo?.aiAnalysis?.hasDamage;
  const severity = photo?.aiAnalysis?.severity;
  const isInvalid = photo?.isInvalid;

  return (
    <div className={`relative overflow-hidden rounded-xl border-2 transition-all dark:bg-gray-750 ${
      isInvalid ? 'border-status-fail' :
      photo && severity === 'severe' ? 'border-status-fail' :
      photo && hasDamage ? 'border-status-warning' :
      photo ? 'border-status-pass' :
      'border-dashed border-gray-200 dark:border-gray-600'
    }`}>
      {photo ? (
        <div className="relative">
          <img
            src={photo.uri}
            alt={prompt.label}
            className={`h-32 w-full object-cover ${isInvalid ? 'opacity-50' : ''} ${photo.pendingUpload ? 'opacity-70' : ''}`}
          />
          <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent px-2 py-1.5">
            {photo.pendingUpload ? (
              <span className="text-[10px] font-bold text-yellow-400">⏳ Pending — offline</span>
            ) : isInvalid ? (
              <span className="text-[10px] font-bold text-status-fail">⚠ INVALID</span>
            ) : severity === 'severe' ? (
              <span className="text-[10px] font-bold text-status-fail">✗ SEVERE</span>
            ) : hasDamage ? (
              <span className="text-[10px] font-bold text-status-warning">⚠ {severity?.toUpperCase()}</span>
            ) : (
              <span className="text-[10px] font-bold text-status-pass">✓ PASS</span>
            )}
            {photo.comparisonNotes?.hasChanges && (
              <span className={`text-[9px] font-bold ${
                photo.comparisonNotes.severity === 'significant' ? 'text-status-fail' :
                photo.comparisonNotes.severity === 'moderate' ? 'text-status-warning' :
                'text-yellow-300'
              }`}>
                △ Changed since last {photo.comparisonNotes.severity}
              </span>
            )}
          </div>
          <button
            onClick={() => cameraRef.current?.click()}
            className="absolute right-1 top-1 rounded-full bg-black/40 p-1 text-white hover:bg-black/60"
          >
            <Camera className="h-3.5 w-3.5" />
          </button>
        </div>
      ) : (
        <div
          className="flex h-32 cursor-pointer flex-col items-center justify-center gap-1 bg-gray-50 dark:bg-gray-700 hover:bg-gray-100 dark:hover:bg-gray-600"
          onClick={() => cameraRef.current?.click()}
        >
          {analyzing ? (
            <>
              <Loader2 className="h-6 w-6 animate-spin text-wire3-green" />
              <p className="text-[10px] text-gray-400">Analyzing...</p>
            </>
          ) : (
            <>
              <span className="text-2xl">{prompt.icon}</span>
              <Camera className="h-4 w-4 text-gray-400" />
              <p className="text-[10px] text-gray-400 dark:text-gray-500">Tap to capture</p>
            </>
          )}
        </div>
      )}

      <div className="px-2 py-1.5 bg-white dark:bg-gray-800">
        <div className="flex items-center justify-between">
          <p className="text-xs font-semibold text-gray-700 dark:text-gray-200">{prompt.label}</p>
          {!prompt.required && (
            <span className="text-[9px] text-gray-400 dark:text-gray-500">Optional</span>
          )}
        </div>
        <p className="text-[10px] text-gray-400 dark:text-gray-500 leading-tight">{prompt.description}</p>
      </div>

      {!photo && !analyzing && (
        <button
          onClick={() => uploadRef.current?.click()}
          className="absolute right-1 top-1 rounded-full bg-white/80 dark:bg-gray-700/80 p-1 text-gray-500 dark:text-gray-300 hover:bg-white dark:hover:bg-gray-700"
        >
          <Upload className="h-3 w-3" />
        </button>
      )}

      <input ref={cameraRef} type="file" accept="image/*" capture="environment" className="hidden"
        onChange={e => { const f = e.target.files?.[0]; if (f) onPhoto(f); e.target.value = ''; }} />
      <input ref={uploadRef} type="file" accept="image/*" className="hidden"
        onChange={e => { const f = e.target.files?.[0]; if (f) onPhoto(f); e.target.value = ''; }} />
    </div>
  );
}
